import type {
  ConversationThreadDetail,
  ConversationThreadListItem,
  MessageCategory,
  MessageDocumentItem,
  MessageRegardingType,
} from "@shared/types/Message";
import apiClient from "@/shared/api/apiClient";
import { API_ROUTES } from "@/constants/apiRoutes";
import { axiosInstance } from "@/shared/api/axios";
function normalizeOptionalProviderId(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

export type ProviderMessagesFeatureState = {
  featureAvailable: boolean;
  upgradeRequired?: boolean;
  hasMessages?: boolean;
  messageCount?: number;
  unreadCount?: number;
  message?: string;
};

export type ProviderMessageThreadsResponse = ProviderMessagesFeatureState & {
  threads: ConversationThreadListItem[];
};

export type ProviderMessageThreadDetailResponse = ProviderMessagesFeatureState & {
  detail: ConversationThreadDetail | null;
};

export async function listProviderMessageThreads(providerId?: string): Promise<ProviderMessageThreadsResponse> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId ? `?providerId=${encodeURIComponent(normalizedProviderId)}` : "";
  const data = await apiClient.get<ProviderMessageThreadsResponse>(
    `${API_ROUTES.providerPortal.messages.threads}${suffix}`,
  );
  return {
    featureAvailable: data.featureAvailable ?? true,
    upgradeRequired: data.upgradeRequired,
    hasMessages: data.hasMessages,
    messageCount: data.messageCount,
    unreadCount: data.unreadCount,
    message: data.message,
    threads: data.threads ?? [],
  };
}

export async function getProviderMessageThread(threadId: string, providerId?: string): Promise<ProviderMessageThreadDetailResponse> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId ? `?providerId=${encodeURIComponent(normalizedProviderId)}` : "";
  const data = await apiClient.get<ProviderMessageThreadDetailResponse>(
    `${API_ROUTES.providerPortal.messages.thread(threadId)}${suffix}`,
  );
  return {
    featureAvailable: data.featureAvailable ?? true,
    upgradeRequired: data.upgradeRequired,
    hasMessages: data.hasMessages,
    messageCount: data.messageCount,
    unreadCount: data.unreadCount,
    message: data.message,
    detail: data.detail ?? null,
  };
}

export async function createProviderMessageThread(input: {
  patientId: string;
  subject?: string | null;
  category?: MessageCategory;
  regardingType?: MessageRegardingType;
  regardingLeadId?: string | null;
  regardingAppointmentId?: string | null;
  messageBody: string;
  messageCategory?: MessageCategory;
}) {
  return apiClient.post<{ threadId: string; messageId: string; message: string }>(
    API_ROUTES.providerPortal.messages.threads,
    input,
  );
}

export async function sendProviderMessage(threadId: string, input: {
  messageBody: string;
  category?: MessageCategory;
}) {
  return apiClient.post<{ message: { id: string } }>(
    API_ROUTES.providerPortal.messages.threadMessages(threadId),
    input,
  );
}

export async function getProviderUnreadMessageCount(providerId?: string): Promise<number> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId ? `?providerId=${encodeURIComponent(normalizedProviderId)}` : "";
  const data = await apiClient.get<{ unreadCount?: number }>(
    `${API_ROUTES.providerPortal.messages.unreadCount}${suffix}`,
  );
  return Number(data.unreadCount ?? 0);
}

export async function listProviderThreadMessageDocuments(threadId: string): Promise<MessageDocumentItem[]> {
  const data = await apiClient.get<{ documents: MessageDocumentItem[] }>(
    API_ROUTES.providerPortal.messages.threadDocuments(threadId),
  );
  return data.documents;
}

export async function uploadProviderMessageDocument(input: {
  threadId: string;
  messageId: string;
  file: File;
}): Promise<{ document: MessageDocumentItem }> {
  const formData = new FormData();
  formData.append("file", input.file);
  const response = await axiosInstance.post<{ document: MessageDocumentItem }>(
    API_ROUTES.providerPortal.messages.messageDocuments(input.threadId, input.messageId),
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
}

export async function getProviderMessageDocumentAccess(input: {
  threadId: string;
  documentId: string;
  action?: "viewed" | "downloaded";
}) {
  const response = await axiosInstance.get<{ accessUrl: string }>(
    API_ROUTES.providerPortal.messages.documentAccess(input.threadId, input.documentId),
    { params: { action: input.action ?? "viewed" } },
  );
  return response.data;
}

function getFileNameFromContentDisposition(value: string | undefined) {
  if (!value) return null;

  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const quotedMatch = value.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) return quotedMatch[1];

  const plainMatch = value.match(/filename=([^;]+)/i);
  return plainMatch?.[1]?.trim() ?? null;
}

export async function fetchProviderMessageDocumentBlob(input: {
  threadId: string;
  documentId: string;
  action?: "viewed" | "downloaded";
}): Promise<{ blob: Blob; fileName: string }> {
  const response = await axiosInstance.get<Blob>(
    API_ROUTES.providerPortal.messages.documentAccess(input.threadId, input.documentId),
    {
      params: { action: input.action ?? "viewed" },
      responseType: "blob",
    },
  );
  const contentDisposition = String(response.headers["content-disposition"] ?? "");

  return {
    blob: response.data,
    fileName:
      getFileNameFromContentDisposition(contentDisposition) ??
      `message-document-${input.action === "downloaded" ? "download" : "view"}`,
  };
}

export async function deleteProviderMessageDocument(threadId: string, documentId: string) {
  return apiClient.delete<{ message: string }>(
    API_ROUTES.providerPortal.messages.documentDelete(threadId, documentId),
  );
}
